import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { analysesApi, type ListAnalysesParams } from '@/api/analyses';
import { queryKeys } from '@/api/query-keys';
import { errorDetail, errorMessage } from '@/api/client';
import { toast } from '@/stores/toast.store';
import { TERMINAL_STATUSES, type AnalysisOptions } from '@/types';

/**
 * Query and mutation hooks for analyses. Components never call the API module
 * directly, so cache invalidation stays in one place.
 */

export function useAnalyses(params: ListAnalysesParams = {}) {
  return useQuery({
    queryKey: queryKeys.analyses(params),
    queryFn: () => analysesApi.list(params),
    // A running analysis changes the list; refresh while any are in flight.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const running = data.items.some((item) => !TERMINAL_STATUSES.includes(item.status));
      return running ? 4000 : false;
    },
  });
}

export function useAnalysis(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.analysis(id ?? ''),
    queryFn: () => analysesApi.get(id!),
    enabled: Boolean(id),
  });
}

export function useAnalysisStyles(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.styles(id ?? ''),
    queryFn: () => analysesApi.styles(id!),
    enabled: Boolean(id) && enabled,
    staleTime: Infinity,
    retry: false,
  });
}

export function useAnalysisComponents(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.components(id ?? ''),
    queryFn: () => analysesApi.components(id!),
    enabled: Boolean(id) && enabled,
    staleTime: Infinity,
    retry: false,
  });
}

export function useAnalysisScreenshots(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.screenshots(id ?? ''),
    queryFn: () => analysesApi.screenshots(id!),
    enabled: Boolean(id) && enabled,
    staleTime: Infinity,
  });
}

export function useAnalysisPages(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.pages(id ?? ''),
    queryFn: () => analysesApi.pages(id!),
    enabled: Boolean(id) && enabled,
  });
}

export function useDesignDocument(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.designDocument(id ?? ''),
    queryFn: () => analysesApi.designDocument(id!),
    enabled: Boolean(id) && enabled,
    retry: false,
  });
}

export function useCreateAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      url: string;
      options?: Partial<AnalysisOptions>;
      projectId?: string | null;
    }) => analysesApi.create(input),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['analyses'] });
      if (result.cachedFrom) {
        toast.info(
          'Reusing a recent analysis',
          'This URL was analyzed recently with the same settings.',
        );
      }
    },
    onError: (error) => toast.error(errorMessage(error), errorDetail(error) ?? undefined),
  });
}

export function useCancelAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analysesApi.cancel(id),
    onSuccess: (analysis) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.analysis(analysis.id) });
      void queryClient.invalidateQueries({ queryKey: ['analyses'] });
      toast.info('Analysis cancelled');
    },
    onError: (error) => toast.error(errorMessage(error), errorDetail(error) ?? undefined),
  });
}

export function useRetryAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analysesApi.retry(id),
    onSuccess: (analysis) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.analysis(analysis.id) });
      void queryClient.invalidateQueries({ queryKey: ['analyses'] });
      toast.success('Analysis re-queued');
    },
    onError: (error) => toast.error(errorMessage(error), errorDetail(error) ?? undefined),
  });
}

export function useDuplicateAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analysesApi.duplicate(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['analyses'] });
      toast.success('Analysis duplicated');
    },
    onError: (error) => toast.error(errorMessage(error), errorDetail(error) ?? undefined),
  });
}

export function useDeleteAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analysesApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['analyses'] });
      toast.success('Analysis deleted');
    },
    onError: (error) => toast.error(errorMessage(error), errorDetail(error) ?? undefined),
  });
}

export function useSaveDesignDocument(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content: string) => analysesApi.saveDesignDocument(id, content),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.designDocument(id) });
      toast.success('design.md saved');
    },
    onError: (error) => toast.error(errorMessage(error), errorDetail(error) ?? undefined),
  });
}

export function useResetDesignDocument(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => analysesApi.resetDesignDocument(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.designDocument(id) });
      toast.success('Restored the generated document');
    },
    onError: (error) => toast.error(errorMessage(error), errorDetail(error) ?? undefined),
  });
}
